import styled from 'styled-components';
import { Collapse, Typography } from '@mui/material';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import { TimelineEventLabel } from 'incidents/components/TimelineEventLabel';
import type { FetchTeamUsersApi } from 'shared/types';
import { fontSize, radius, space } from '@elementary/common-ui/style/utils';
import type { EventGroup, IncidentDetailEvent } from '../../types';
import { formatDate, getEventIcon, getEventLink } from '../utils';

const EventRow = styled(Flex)`
  gap: ${space('md')};
  align-items: center;
`;

const IconWrapper = styled(Flex)`
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LineWrapper = styled(Flex)`
  width: 24px;
  justify-content: center;
  flex-shrink: 0;
`;

const VerticalLine = styled.div`
  width: 1px;
  height: 12px;
  background-color: ${({ theme }) => theme.palette.surface.strong};
`;

const ShowMoreLineWrapper = styled(Flex)`
  width: 24px;
  justify-content: center;
  flex-shrink: 0;
`;

const ShowMoreLine = styled.div`
  width: 1px;
  background-color: ${({ theme }) => theme.palette.surface.strong};
`;

const EventContent = styled(Flex)`
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: ${space('sm')};
`;

const Dot = styled.span`
  color: ${({ theme }) => theme.palette.content.tertiary};
  font-size: ${fontSize('base')};
`;

const EventTime = styled(Typography)`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.tertiary};
  white-space: nowrap;
`;

const ShowMoreLink = styled(Typography)`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.tertiary};
  cursor: pointer;
  white-space: nowrap;
  text-decoration: underline;
`;

const ExpandedSection = styled(Flex)`
  gap: ${space('md')};
  align-items: stretch;
`;

const ExpandedContentWrapper = styled(FlexColumn)`
  flex: 1;
  gap: ${space('sm')};
  min-width: 0;
`;

const ExpandedEventsBox = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.surface.soft};
  border-radius: ${radius('lg')};
  padding: ${space('sm')};
  gap: ${space('sm')};
  max-height: 280px;
  overflow-y: auto;
`;

const ExpandedEventItem = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
`;

const DotComponent = () => <Dot>&middot;</Dot>;

type Props = {
  group: EventGroup;
  isExpanded: boolean;
  isLastGroup: boolean;
  onToggle: () => void;
  usersData?: FetchTeamUsersApi;
  onOpenIncident?: (incidentId: string) => void;
};

const TimelineEventGroup = ({
  group,
  isLastGroup,
  onToggle,
  isExpanded,
  usersData,
  onOpenIncident,
}: Props) => {
  const hasMultiple = group.events.length > 1;
  const firstEvent = group.events[0];
  const restEvents = group.events.slice(1);
  const firstDate = formatDate(group.firstEvent.eventTime);
  const lastDate = formatDate(group.lastEvent.eventTime);
  const dateDisplay = hasMultiple ? `${lastDate} - ${firstDate}` : firstDate;

  return (
    <>
      <EventRow>
        <IconWrapper>{getEventIcon(firstEvent, usersData)}</IconWrapper>
        <EventContent>
          <TimelineEventLabel label={group.label} link={group.link} />
          <DotComponent />
          <EventTime>{dateDisplay}</EventTime>
        </EventContent>
      </EventRow>
      {hasMultiple && (
        <ExpandedSection>
          {!isLastGroup && (
            <ShowMoreLineWrapper>
              <ShowMoreLine />
            </ShowMoreLineWrapper>
          )}
          <ExpandedContentWrapper sx={{ marginLeft: isLastGroup ? '36px' : 0 }}>
            <ShowMoreLink onClick={onToggle}>
              {isExpanded
                ? `Hide ${group.events.length} events`
                : `Show all ${group.events.length} events`}
            </ShowMoreLink>
            <Collapse in={isExpanded} timeout={200}>
              {restEvents.length > 0 && (
                <ExpandedEventsBox>
                  {restEvents.map((event: IncidentDetailEvent) => (
                    <ExpandedEventItem key={event.id}>
                      <TimelineEventLabel
                        label={group.label}
                        link={getEventLink(event, usersData, {
                          onOpenIncident,
                        })}
                      />
                      <DotComponent />
                      <EventTime>{formatDate(event.eventTime)}</EventTime>
                    </ExpandedEventItem>
                  ))}
                </ExpandedEventsBox>
              )}
            </Collapse>
          </ExpandedContentWrapper>
        </ExpandedSection>
      )}
      {!isLastGroup && (
        <LineWrapper>
          <VerticalLine />
        </LineWrapper>
      )}
    </>
  );
};

export default TimelineEventGroup;
